import React, { ReactNode } from "react";

interface ButtonProps {
  children: ReactNode;
  size?: "sm" | "md" | "lg";
  variant?:
    | "primary"
    | "outline"
    | "error"
    | "success"
    | "secondary"
    | "info"
    | "glass"
    | "wizardNext"
    | "wizardPrev";
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  type?: "button" | "submit" | "reset";
  loading?: boolean;
  role?: string;
  fullWidth?: boolean;
}

const Button: React.FC<ButtonProps> = ({
  children,
  size = "md",
  variant = "primary",
  startIcon,
  endIcon,
  onClick,
  className = "",
  disabled = false,
  type = "button",
  loading = false,
  role,
  fullWidth = false,
}) => {
  // sizes
  const sizeClasses = {
    sm: "h-9 px-4 text-sm",
    md: "h-11 px-5 text-sm",
    lg: "h-12 px-6 text-base",
  };

  // variants
  const variantClasses = {
    primary:
      "bg-brand-600 text-white shadow-md shadow-brand-600/20 " +
      "hover:bg-brand-700 hover:shadow-lg hover:shadow-brand-600/30",

    success:
      "bg-green-700 text-white shadow-md shadow-green-600/20 " +
      "hover:bg-green-700 hover:shadow-lg hover:shadow-green-600/30",

    error:
      "bg-red-700 text-white shadow-md shadow-red-600/20 " +
      "hover:bg-red-800 hover:shadow-lg hover:shadow-red-600/30 ",

    info:
      "bg-blue-600 text-white shadow-md shadow-blue-600/20 " +
      "hover:bg-blue-700 hover:shadow-lg hover:shadow-blue-600/30",

    secondary:
      "bg-indigo-100 text-indigo-700 border border-indigo-200 " +
      "hover:bg-indigo-200 dark:bg-indigo-800 dark:border-indigo-700 dark:text-indigo-200 dark:hover:bg-indigo-700",

    outline:
      "border border-slate-300 bg-white text-slate-700 " +
      "hover:bg-slate-50 hover:border-slate-400 " +
      "dark:bg-slate-900 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800",

    glass:
      "border border-white/20 " +
      "bg-white/10 backdrop-blur-2xl " +
      "text-black " +
      "shadow-[0_8px_32px_rgba(0,0,0,0.12)] " +
      "hover:bg-white/15 hover:border-white/30",

    wizardNext:
      "bg-brand-600 text-white shadow-md shadow-brand-600/20 " +
      "hover:bg-brand-700 hover:shadow-lg hover:shadow-brand-600/30",

    wizardPrev:
      "border border-slate-300 bg-white text-slate-700 " +
      "hover:bg-slate-50 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-200",
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      type={type}
      role={role}
      className={`
  group
  relative
  inline-flex
  items-center
  justify-center
  gap-2
  overflow-hidden
  rounded-xl
  font-medium
  transition-all
  duration-200
  hover:-translate-y-px
  active:translate-y-0
  active:scale-[0.98]
  disabled:pointer-events-none
  disabled:opacity-50
  disabled:shadow-none
  ${sizeClasses[size]}
  ${variantClasses[variant]}
  ${fullWidth ? "w-full" : ""}
  ${className}
`}
    >
      {/* glow effect */}
      <span className="absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
        <span className="absolute -left-20 top-0 h-full w-8 rotate-12 bg-white/10 blur-lg transition-all duration-500 group-hover:left-[130%]" />
      </span>

      {/* content */}
      <span
        className={`relative z-10 flex items-center gap-2 transition-all duration-200 ${
          loading ? "opacity-0" : "opacity-100"
        }`}
      >
        {startIcon && (
          <span className="transition-transform duration-300 group-hover:scale-110">
            {startIcon}
          </span>
        )}

        <span>{children}</span>

        {endIcon && (
          <span className="transition-transform duration-300 group-hover:translate-x-1 rtl:group-hover:-translate-x-1">
            {endIcon}
          </span>
        )}
      </span>

      {/* loading */}
      {loading && (
        <span className="absolute inset-0 z-20 flex items-center justify-center">
          <div className="loader w-3" />
        </span>
      )}
    </button>
  );
};

export default Button;
